import type { FC } from 'react';
import React, { useState, useCallback } from 'react';

import { cssMap } from '@atlaskit/css';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { EditorBreadcrumbs } from '@confluence/editor-breadcrumbs/entry-points/EditorBreadcrumbs.ts';
import { EditorAppearanceButtonNext } from '@confluence/editor-appearance-button/entry-points/EditorAppearanceButtonNext.ts';
import { EditorByLineArea } from '@confluence/content-header/entry-points/EditorByLineArea.js';
import { TitleAreaContentComponents } from '@confluence/title-area-content-components/entry-points/TitleAreaContentComponents.tsx';
import { useContentComponentsContext } from '@confluence/title-area-content-components/entry-points/ContentComponentsContext.tsx';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { ApprovalBanner } from '@confluence/approvals/entry-points/ApprovalBanner.ts';
import { usePrimaryToolbarContext } from '@confluence/editor-primary-toolbar-components/entry-points/PrimaryToolbarContext.ts';
import type { ContentTypeType } from '@confluence/editor-features/entry-points/types.ts';
import { useSpaceType } from '@confluence/create-space/entry-points/useSpaceType.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import type { ContentClassificationMetadata } from '@confluence/data-classification/entry-points/useContentClassification.ts';
import { useTransitionId } from '@confluence/route-manager/entry-points/RouteState.ts';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';
import { useSpaceHomepageId } from '@confluence/space-utils/entry-points/useSpaceHomepageId.ts';
import { useNativeCollabConnected } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

const styles = cssMap({
	bannerSpacing: {
		paddingBlockStart: token('space.250'),
	},
});

export type TitleAreaContentComponentsWrapperProps = {
	contentId: string;
	cloudId: string;
	linkCreation: boolean;
	focusEditor: () => void;
	onFocusChanged: (focusChanged: boolean) => void;
	debounceOnChange: boolean;
	userOffline: boolean;
	templateSuggestionsRef?: React.RefObject<HTMLDivElement> | null;
	templateButtonRef?: React.RefObject<HTMLElement> | null;
	hideSpinner?: () => void;
	viewMode?: boolean;
	contentClassificationMetadata?: ContentClassificationMetadata;
	version?: number;
	isEditorEmpty?: boolean;
	collabEditInitialized?: boolean;
	editorApi?: FullPageEditorPresetAPI;
	/** Forwarded to `LivePagesTitle` for the `.md`-suffix → markdown-mode gesture. */
	editorActions?: EditorActions;
	editorSessionId?: string | null;
	isSeamlessTransitionInProgress?: boolean;
	hasByLineContributors: boolean;
	hasByLineExtensions: boolean;
	hasReactions: boolean;
	hasAudioByline?: boolean;
	customPlaceholderOverride?: string;
};

export const TitleAreaContentComponentsWrapper: FC<TitleAreaContentComponentsWrapperProps> = ({
	contentId,
	cloudId,
	linkCreation,
	focusEditor,
	onFocusChanged,
	debounceOnChange,
	userOffline,
	templateSuggestionsRef,
	templateButtonRef,
	viewMode,
	contentClassificationMetadata,
	version,
	isEditorEmpty,
	collabEditInitialized,
	editorApi,
	editorActions,
	editorSessionId,
	isSeamlessTransitionInProgress = false,
	hasByLineContributors,
	hasByLineExtensions,
	hasReactions,
	hasAudioByline,
	customPlaceholderOverride,
}) => {
	const isLivePage = useIsCurrentPageLive();
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const [{ collabEditConnected }] = useNativeCollabConnected();
	const {
		collabEditState,
		contentType,
		shouldRenderPreview,
		isPublishingContent,
		isUnpublishedDraft,
		spaceKey,
	} = usePrimaryToolbarContext();

	const {
		contentComponentsTitle,
		contentComponentsDisabled,
		isDraftLockedForEditing,
		handleTitleChange,
		breadcrumbsClickHandler,
	} = useContentComponentsContext();

	const { isKnowledgeBaseSpace } = useSpaceType({ spaceKey });
	const homepageId = useSpaceHomepageId(spaceKey);
	const isSpaceOverview = homepageId === contentId;

	const transitionId = useTransitionId();
	const isInitialLoad = !transitionId;
	const renderServerPlaceholder = useRenderServerPlaceholder(true);
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const ssrOrInitial = !!process.env.REACT_SSR || (isInitialLoad && window.__SSR_RENDERED__);

	// For Live Pages, title component doesn't send metadata to NCS via client websocket message broadcast, hence is not dependent on collab being connected
	const disableTitleAsNotConnected =
		contentComponentsDisabled || (!isLivePage && !collabEditConnected);
	const isTitleDisabled =
		(!ssrOrInitial && (disableTitleAsNotConnected || linkCreation)) ||
		(!renderServerPlaceholder &&
			!isLivePage &&
			!!isDraftLockedForEditing &&
			expValEquals('confluence_approval_lock_write_surfaces', 'isEnabled', true));

	const [isTitleGenerating, setIsTitleGenerating] = useState(false);
	const handleTitleGeneratingChange = useCallback((isGenerating: boolean) => {
		setIsTitleGenerating(isGenerating);
	}, []);

	const EditorAppearanceButton = (
		<EditorAppearanceButtonNext
			contentId={contentId}
			disabled={contentComponentsDisabled || !collabEditConnected}
			isStatusDisconnected={collabEditState.collabEditInternetLost}
		/>
	);

	const breadcrumbsComponent = (
		<EditorBreadcrumbs
			key="breadcrumbs-line"
			contentId={contentId}
			contentType={contentType as ContentTypeType}
			EditContentAppearanceButton={EditorAppearanceButton}
			breadcrumbsClickHandler={breadcrumbsClickHandler}
			isPublishingContent={isPublishingContent}
			userOffline={userOffline}
			collabEditInitialized={collabEditState.collabEditInitialized}
			moveLocationConfig={{
				spaceKey,
				isUnpublishedDraft,
			}}
			isKnowledgeBaseSpace={isKnowledgeBaseSpace}
		/>
	);

	const shouldShowEditorByLine = isEmbeddedConfluence
		? hasByLineContributors || hasByLineExtensions || hasReactions
		: true;

	const editorByLineComponent = shouldShowEditorByLine ? (
		<>
			<EditorByLineArea
				contentId={contentId}
				contentType={contentType}
				isPublishingContent={isPublishingContent}
				userOffline={userOffline}
				isKnowledgeBaseSpace={isKnowledgeBaseSpace}
				viewMode={viewMode}
				editorApi={editorApi}
				isUnpublishedDraft={isUnpublishedDraft}
				isSpaceOverview={isSpaceOverview}
				hasByLineContributors={hasByLineContributors}
				hasByLineExtensions={hasByLineExtensions}
				hasReactions={hasReactions}
				hasAudioByline={hasAudioByline}
			/>
			{expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
				<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
					<Box xcss={styles.bannerSpacing}>
						<ApprovalBanner />
					</Box>
				</ErrorBoundary>
			)}
		</>
	) : null;

	return (
		<TitleAreaContentComponents
			key="title-area-content-components"
			title={contentComponentsTitle}
			breadcrumbsComponent={breadcrumbsComponent}
			contentId={contentId}
			cloudId={cloudId}
			isDisabled={isTitleDisabled}
			onChange={handleTitleChange}
			focusEditor={focusEditor}
			onFocusChanged={onFocusChanged}
			debounceOnChange={debounceOnChange}
			contentType={contentType as ContentTypeType}
			userOffline={userOffline}
			isInPreview={shouldRenderPreview}
			isPublishingContent={isPublishingContent}
			isUnpublishedDraft={isUnpublishedDraft}
			templateSuggestionsRef={templateSuggestionsRef}
			templateButtonRef={templateButtonRef}
			contentTopperComponent={editorByLineComponent}
			linkCreation={linkCreation}
			viewMode={viewMode}
			contentClassificationMetadata={contentClassificationMetadata}
			version={version}
			isEditorEmpty={isEditorEmpty}
			collabEditInitialized={collabEditInitialized}
			contentComponentsDisabled={contentComponentsDisabled}
			isDraftLockedForEditing={isDraftLockedForEditing}
			isStatusDisconnected={collabEditState.collabEditInternetLost}
			editorApi={editorApi}
			editorActions={editorActions}
			editorSessionId={editorSessionId}
			isSeamlessTransitionInProgress={isSeamlessTransitionInProgress}
			isTitleGenerating={isTitleGenerating}
			onTitleGeneratingChange={handleTitleGeneratingChange}
			isEmbeddedConfluence={isEmbeddedConfluence}
			customPlaceholderOverride={customPlaceholderOverride}
			isDisabledTextBlack={isDraftLockedForEditing}
		/>
	);
};
